import React, { useContext } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { UserContext } from 'context/UserContext';

import Form from 'components/common/Form';
import Select from 'components/common/Form/Select';
import Input from 'components/common/Form/Input';
import Button, { ButtonStyles } from 'components/common/Form/Button';

import Currencies, { HourlyRates } from 'constants/Finances';
import { convertToOptions, convertToValue } from 'helpers/SelectUtils';

import { isModerator, isTranslator } from 'constants/UserRoles';

import style from 'components/Welcome/Form/Form.module.scss';
import MultiSwitch from 'components/common/Form/MultiSwitch/MultiSwitch';
import QuestionMarkTooltip from 'components/common/QuestionMarkTooltip/QuestionMarkTooltip';

export default function PaymentDetailsForm({
    onChange,
    onPreviousStep,
    onNextStep,
    data,
    isLastStep,
    errors,
    profileTypeToggle,
    isMedical,
    isBusiness,
    validate,
    onChangeHourlyRate,
    isModal,
}) {
    const { costPerHour, currency, full, summary, hourlyRate } = data;
    const { user } = useContext(UserContext);
    const { t } = useTranslation();
    const isTranslatorOrModerator = isTranslator(user.role) || isModerator(user.role);

    return (
        <div className={style.profileFormContainer}>
            <Form className={cn(style.form, { [style.wideForm]: isTranslatorOrModerator, [style.modal]: isModal })}>
                <div className={cn(style.inlineFieldsContainer, style.withTopMargin)}>
                    <div>
                        <span>{t('welcome:form.labels.currency')}</span>
                        <MultiSwitch
                            options={convertToOptions(Currencies)}
                            value={currency}
                            onChange={onChange.bind(this, 'currency')}
                        />
                    </div>
                    <div className={cn(style.markWrapper, style.topMargin)}>
                        <QuestionMarkTooltip
                            text={t('welcome:form.tooltips.currency')}
                            classes={{ tooltip: style.tooltip }}
                            markClasses={style.questionMarkIcon}
                        />
                    </div>
                </div>
                {/* {isRespondent(user.role) && (
                    <div className={style.inlineFieldsContainer}>
                        <Select
                            label={<span style={{ whiteSpace: 'nowrap' }}>{t('welcome:form.labels.perHour')}</span>}
                            placeholder={t('welcome:text.hourlyRate')}
                            options={convertToOptions(HourlyRates)}
                            value={convertToValue(costPerHour, HourlyRates)}
                            onChange={onChange.bind(this, 'costPerHour')}
                            error={t(errors.costPerHour)}
                            labelClassName={style.labelClassName}
                            onBlur={validate.bind(this, data, 'costPerHour')}
                        />
                    </div>
                )} */}

                {isTranslatorOrModerator && (
                    <>
                        <div className={style.subLabel}>{t('welcome:form.labels.hourlyRate')}</div>
                        <div className={style.inlineFieldsContainer}>
                            <Select
                                label={t('interviewTypes.offline-home') + ':'}
                                placeholder={t('welcome:form.placeholders.selectPrice')}
                                options={convertToOptions(HourlyRates)}
                                value={convertToValue(hourlyRate['offline-home'], HourlyRates)}
                                onChange={onChangeHourlyRate.bind(this, 'offline-home')}
                                error={t(errors['offline-home'])}
                                labelClassName={cn(style.labelClassName, style.smallText)}
                                onBlur={validate.bind(this, data, 'offline-home')}
                            />
                            <Select
                                label={t('interviewTypes.vfgd') + ':'}
                                placeholder={t('welcome:form.placeholders.selectPrice')}
                                options={convertToOptions(HourlyRates)}
                                value={convertToValue(hourlyRate['vfgd'], HourlyRates)}
                                onChange={onChangeHourlyRate.bind(this, 'vfgd')}
                                error={t(errors['vfgd'])}
                                labelClassName={cn(style.labelClassName, style.smallText)}
                                onBlur={validate.bind(this, data, 'vfgd')}
                            />
                            <Select
                                label={t('interviewTypes.offline-interview') + ':'}
                                placeholder={t('welcome:form.placeholders.selectPrice')}
                                options={convertToOptions(HourlyRates)}
                                value={convertToValue(hourlyRate['offline-interview'], HourlyRates)}
                                onChange={onChangeHourlyRate.bind(this, 'offline-interview')}
                                error={t(errors['offline-interview'])}
                                labelClassName={cn(style.labelClassName, style.smallText)}
                                onBlur={validate.bind(this, data, 'offline-interview')}
                            />
                        </div>
                        <div className={style.inlineFieldsContainer}>
                            <Select
                                label={t('interviewTypes.video-idi') + ':'}
                                placeholder={t('welcome:form.placeholders.selectPrice')}
                                options={convertToOptions(HourlyRates)}
                                value={convertToValue(hourlyRate['video-idi'], HourlyRates)}
                                onChange={onChangeHourlyRate.bind(this, 'video-idi')}
                                error={t(errors['video-idi'])}
                                labelClassName={cn(style.labelClassName, style.smallText)}
                                onBlur={validate.bind(this, data, 'video-idi')}
                            />
                            <Select
                                label={t('interviewTypes.offline-focus-group') + ':'}
                                placeholder={t('welcome:form.placeholders.selectPrice')}
                                options={convertToOptions(HourlyRates)}
                                value={convertToValue(hourlyRate['offline-focus-group'], HourlyRates)}
                                onChange={onChangeHourlyRate.bind(this, 'offline-focus-group')}
                                error={t(errors['offline-focus-group'])}
                                labelClassName={cn(style.labelClassName, style.smallText)}
                                onBlur={validate.bind(this, data, 'offline-focus-group')}
                            />
                            {isModerator(user.role) ? (
                                <Select
                                    label={t('interviewTypes.online') + ':'}
                                    placeholder={t('welcome:form.placeholders.selectPrice')}
                                    options={convertToOptions(HourlyRates)}
                                    value={convertToValue(hourlyRate['online'], HourlyRates)}
                                    onChange={onChangeHourlyRate.bind(this, 'online')}
                                    error={t(errors['online'])}
                                    labelClassName={cn(style.labelClassName, style.smallText)}
                                    onBlur={validate.bind(this, data, 'online')}
                                />
                            ) : (
                                <div />
                            )}
                        </div>
                        {isModerator(user.role) && (
                            <>
                                <div className={style.subLabel}>{t('welcome:form.labels.reportRate')}</div>
                                <div className={style.inlineFieldsContainer}>
                                    <Input
                                        label={t('welcome:form.labels.summaryRate')}
                                        placeholder={t('welcome:form.placeholders.selectPrice')}
                                        value={summary}
                                        onChange={onChange.bind(this, 'summary')}
                                        error={t(errors.summary)}
                                        labelClassName={style.labelClassName}
                                        onBlur={validate.bind(this, data, 'summary')}
                                    />
                                    <Input
                                        label={t('welcome:form.labels.fullRate')}
                                        placeholder={t('welcome:form.placeholders.selectPrice')}
                                        value={full}
                                        onChange={onChange.bind(this, 'full')}
                                        error={t(errors.full)}
                                        labelClassName={style.labelClassName}
                                        onBlur={validate.bind(this, data, 'full')}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
                {isLastStep && <div className={style.commonError}>{errors.common}</div>}
                <div className={cn(style.buttonWrapper, style.withBottomMargin)}>
                    <Button
                        text={isLastStep ? t('welcome:buttons.getStarted') : t('welcome:buttons.continue')}
                        onClick={onNextStep}
                        btnStyle={ButtonStyles.primary}
                    />
                </div>
            </Form>
        </div>
    );
}
